import Vue from 'vue';
import VueRouter from 'vue-router';

import store from '@/store';
import { USER_LEVEL } from '@/utils/constants';

Vue.use(VueRouter);

const ifNotAuthenticated = (to, from, next) => {
  if (!localStorage.getItem('_t')) {
    next();
    return;
  }
  const path = from?.fullPath || from?.path;
  const target = path === '/' ? '/dashboard' : path;
  next(target);
};

const routes = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/login',
    name: 'Login',
    beforeEnter: ifNotAuthenticated,
    component: () => import('../views/Login/Login.vue'),
  },
  {
    path: '/register',
    name: 'Register',
    beforeEnter: ifNotAuthenticated,
    component: () => import('../views/Register/Register.vue'),
  },
  {
    path: '/register/completed',
    name: 'RegisterCompleted',
    beforeEnter: ifNotAuthenticated,
    component: () => import('../views/RegisterCompleted/RegisterCompleted.vue'),
  },
  {
    path: '/reset_password',
    name: 'ResetPassword',
    beforeEnter: ifNotAuthenticated,
    component: () => import('../views/ResetPassword/ResetPassword.vue'),
  },
  {
    path: '/reset-password-verification',
    name: 'ResetPasswordVerification',
    component: () => import('../views/ResetPasswordVerification/ResetPasswordVerification.vue'),
  },
  {
    path: '/temp-report/:shareKey?',
    name: 'TempReport',
    component: () => import('../views/TempReport/TempReport.vue'),
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('../views/Dashboard/Dashboard.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/order/create/:draftId?',
    name: 'CreateOrder',
    component: () => import('../views/CreateOrder/CreateOrder.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/order/schedule',
    name: 'Schedule',
    component: () => import('../views/Schedule/Schedule.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/order/schedule/create/:scheduleId?',
    name: 'CreateSchedule',
    component: () => import('../views/CreateSchedule/CreateSchedule.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/order/schedule/view/:scheduleId?',
    name: 'ViewSchedule',
    component: () => import('../views/ViewSchedule/ViewSchedule.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/order/schedule/edit/:scheduleId?',
    name: 'EditSchedule',
    component: () => import('../views/EditSchedule/EditSchedule.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/order/edit/:orderId?',
    name: 'EditOrder',
    component: () => import('../views/EditOrder/EditOrder.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/order/:orderId(\\d+)',
    name: 'OrderPreview',
    component: () => import('../views/OrderPreview/OrderPreview.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/order',
    name: 'OrderList',
    alias: '/order/list',
    component: () => import('../views/OrderList/OrderList.vue'),
    meta: {
      requiresAuth: true,
    },
    props: true,
  },
  {
    path: '/send_verify_email',
    name: 'SendVerifyEmail',
    beforeEnter: ifNotAuthenticated,
    component: () => import('../views/SendVerifyEmail/SendVerifyEmail.vue'),
  },
  {
    path: '/store/:action(create|list)',
    name: 'Store',
    component: () => import('../views/Store/Store.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/store/:storeId(\\d+)/menus/:action?',
    name: 'StoreMenus',
    component: () => import('../views/StoreMenus/StoreMenus.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/store/:storeId(\\d+)/:action?/:actionId?',
    name: 'StoreEditor',
    component: () => import('../views/Store/Store.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/member/list',
    name: 'MemberList',
    component: () => import('../views/Member/Member.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/member/:action(create|batch-create)',
    name: 'Member',
    component: () => import('../views/Member/Member.vue'),
    meta: {
      requiresAuth: true,
      minRole: USER_LEVEL.ADMIN,
    },
  },
  {
    path: '/member/:memberId(\\d+)/:action',
    name: 'MemberEditor',
    component: () => import('../views/Member/Member.vue'),
    meta: {
      requiresAuth: true,
      minRole: USER_LEVEL.ADMIN,
    },
  },
  {
    path: '/statistics/:type(group_buyings|members)?/:id(\\d+)?/',
    name: 'Statistic',
    component: () => import('../views/Statistics/Statistics.vue'),
    meta: {
      requiresAuth: true,
      minRole: USER_LEVEL.ADMIN,
    },
  },
  {
    path: '/setting/:action(plans|payment)?',
    name: 'Setting',
    component: () => import('../views/Setting/Setting.vue'),
    meta: {
      requiresAuth: true,
      minRole: USER_LEVEL.OWNER,
    },
  },
  {
    path: '/user-info',
    name: 'UserInfo',
    component: () => import('../views/UserInfo/UserInfo.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../views/NotFound/NotFound.vue'),
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach(async (to, from, next) => {
  const { matched } = to;
  const requiresAuth = matched.some(({ meta }) => meta.requiresAuth);
  const minRole = matched.some(({ meta }) => meta?.minRole);
  const isAuthenticated = store.getters['user/isAuthenticated'];

  /* Refresh user role */
  if (requiresAuth) {
    try {
      await store.dispatch('user/SET_USER_ROLE');
    } catch {
      store.dispatch('user/USER_LOGOUT');
      /* Session expired or any exception */
      next({ path: '/login' });
      return;
    }
  }

  if (requiresAuth && !isAuthenticated) {
    store.dispatch('user/USER_LOGOUT');
    next({
      path: '/login',
      query: { redirect: to.fullPath },
    });
    return;
  }

  if (minRole) {
    const userRole = store.getters['user/currentRole'];
    if (userRole < minRole) {
      next({ path: '/404' });
      return;
    }
  }
  next();
});

export default router;
